import type { TranslationTree } from '../translations';

export const en: TranslationTree = {
  login: {
    title: 'Login',
    email: 'Email',
    password: 'Password',
    thanks: 'Thanks',
    submit: 'Sign in',
    submitting: 'Signing in...',
    requiredFields: 'Please fill in all fields',
    invalidCredentials: 'Login failed. Check your credentials.',
    planInactive: 'Your company plan is inactive. Contact the administrator.',
    forgotPassword: 'Forgot my password?',
    forgotPasswordTitle: 'Recover password',
    forgotPasswordDescription: 'Enter your email to receive a recovery code.',
    sendResetCode: 'Send code',
    sendingResetCode: 'Sending...',
    backToLogin: 'Back to login',
    resetCodeSent: 'We sent a code to your email.',
    resetPasswordTitle: 'New password',
    resetPasswordDescription: 'Enter the code you received and set your new password.',
    resetCode: 'Code',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    resetPassword: 'Change password',
    resettingPassword: 'Changing...',
    resetPasswordSuccessTitle: 'Password changed',
    resetPasswordSuccessDescription: 'Your password has been updated successfully. You can sign in again.',
    invalidEmail: 'Enter a valid email',
    resetCodeRequired: 'Enter the recovery code',
    invalidResetCode: 'Code must contain 6 digits',
    passwordRequired: 'Enter and confirm your new password',
    passwordMinLength: 'Password must be at least 6 characters',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordResetRequestError: 'Could not send the recovery code. Please try again.',
    passwordResetError: 'Could not change the password. Check the code and try again.',
    languageLabel: 'Language',
    portugueseLanguage: 'Portuguese',
    englishLanguage: 'English',
  },
  common: {
    loading: 'Loading...',
    loadingData: 'Loading data...',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    confirm: 'Confirm',
    back: 'Back',
    edit: 'Edit',
    delete: 'Delete',
    remove: 'Remove',
    processing: 'Processing...',
    notAvailable: 'N/A',
    requiredFieldsHint: '* Required fields',
    active: 'Active',
    inactive: 'Inactive',
    actions: 'Actions',
    company: 'Company',
    companies: 'Companies',
    language: 'Language',
    role: 'Role',
    name: 'Name',
    email: 'Email',
    password: 'Password',
    description: 'Description',
    cnpj: 'CNPJ',
  },
  menu: {
    rolePrefix: 'Role:',
    loading: 'Loading...',
    menu: 'Menu',
    home: 'Home',
    generateReport: 'Generate Report',
    reportShort: 'Report',
    adminPanel: 'Admin Panel',
    adminShort: 'Admin',
    companies: 'Companies',
    profile: 'Profile',
    logout: 'Sign out',
    logoutShort: 'Exit',
  },
  accessibility: {
    enableScreenReader: 'Enable screen reader',
    disableScreenReader: 'Disable screen reader',
    screenReaderEnabled: 'Screen reader enabled',
    screenReaderDisabled: 'Screen reader disabled',
    fontSizeControls: 'Font size controls',
    decreaseFontSize: 'Decrease font size',
    increaseFontSize: 'Increase font size',
    decreaseFontTitle: 'Decrease font',
    increaseFontTitle: 'Increase font',
    fontIncreased: 'Font increased. Level {level} of 4',
    fontDecreased: 'Font decreased. Level {level} of 4',
  },
  register: {
    testId: 'Test ID',
    participantName: 'Participant name',
    birthDate: 'Date of birth',
    startTest: 'Start test',
    checkingLicenses: 'Checking available licenses...',
    licenseLimit: 'License limit reached.',
    licenseContact: 'To continue, request new licenses at contato@causale.org or by WhatsApp +55 53 99189-9532.',
    companyNotIdentified: 'Could not identify the user company.',
    licenseValidationError: 'Could not validate available licenses. Reload the page and try again.',
  },
  instruction: {
    firstParagraph: 'Now you will do a choice activity.',
    secondParagraph: 'In each question, you will have two options: one shows an amount of money you could receive now and the other shows a larger amount, but one you would receive only after some time.',
    thirdParagraph: 'There is no right or wrong answer. What matters is that you answer based on what you would really prefer if it were real.',
    start: 'Start',
  },
  resetPassword: {
    loading: 'Loading...',
    missingEmail: 'Email not provided. Please sign in again.',
    userNotFound: 'User not found.',
    loadUserError: 'Could not load user data.',
    title: 'Set New Password',
    greeting: 'Hello,',
    firstAccessSubtitle: 'This is your first access. Please set a new password.',
    oldPassword: 'Old Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm New Password',
    requirementsTitle: 'Password requirements:',
    minLengthRequirement: 'At least 6 characters',
    matchRequirement: 'Passwords must match',
    fillAllFields: 'Fill in all fields',
    userNotIdentified: 'User not identified',
    resetError: 'Could not reset password. Please try again.',
    saving: 'Saving...',
    submit: 'Set Password',
    footer: 'After setting your password, you will be redirected to login.',
  },
  reports: {
    loadError: 'Could not load information. Please sign in again.',
    selectCompanyFullPeriod: 'Select a company to fill the full period.',
    companyCreationDateMissing: 'Could not identify the company creation date.',
    companyCreationDateInvalid: 'The company creation date is invalid.',
    fillFullPeriodError: 'Could not fill the full period. Please try again.',
    selectCompanyReport: 'Select a company to generate the report.',
    defaultCompanyFileName: 'company',
    reportSuccess: 'Report generated successfully!',
    recordsFound: 'record(s) found.',
    noRecordsFound: 'No records found for the selected period.\n\nCheck:\n- If data exists for the company\n- If the selected period is correct',
    reportError: 'Could not generate the report. Please try again.',
    selectCompanyAndPeriod: 'Select the company and period',
    timelineSubtitle: 'Adjust the filters in each step before downloading the report.',
    notSelected: 'Not selected',
    selectPeriod: 'Select the period',
    company: 'Company',
    startDate: 'Start Date',
    endDate: 'End Date',
    loadingPeriod: 'Loading period...',
    fullPeriod: 'Full period',
    generating: 'Generating...',
    downloadReport: 'Download Report',
    backHome: 'Back to start',
    notAvailable: 'N/A',
    reportFilePrefix: 'delay_discounting_report',
    filePeriodSeparator: 'to',
    dataSheet: 'Data',
    documentationSheet: 'Documentation',
    field: 'Field',
    fieldDescription: 'Description',
    recordId: 'Record ID',
    participantName: 'Name',
    birthDate: 'Birth Date',
    applicator: 'Applicator',
    immediateValue: 'Immediate Value',
    delayedValue: 'Delayed Value',
    responseIndex: 'R{number} Index',
    responseTime: 'R{number} Time (s)',
    responseChoice: 'R{number} Choice',
    startedAt: 'Started at',
    completedAt: 'Completed at',
    totalTimeSeconds: 'Total Time (s)',
    immediateChoice: 'Immediate',
    delayedChoice: 'Delayed',
    recordIdDescription: 'Unique system record identifier, automatically generated for each participant',
    participantNameDescription: 'Participant full name',
    birthDateDescription: 'Participant birth date (format: DD/MM/YYYY or YYYY-MM-DD)',
    applicatorDescription: 'Name of the user who applied the test',
    immediateValueDescription: 'Monetary value of the immediate reward presented in the choices',
    delayedValueDescription: 'Monetary value of the delayed reward presented in the choices',
    responseIndexDescription: 'Index of the delay option presented in Response {number}',
    responseTimeDescription: 'Time in seconds the participant took to make the choice in Response {number}',
    responseChoiceDescription: 'Choice made by the participant in Response {number} (values: "Immediate" or "Delayed")',
    ed50Description: 'Effective delay where the participant is indifferent between the options. Point where the delayed reward has 50% of the immediate reward value.',
    kDescription: 'K parameter of the hyperbolic delay discounting function. Higher values indicate greater impulsivity/discounting.',
    startedAtDescription: 'Date and time when the participant started the task',
    completedAtDescription: 'Date and time when the participant completed the task',
    totalTimeDescription: 'Total time in seconds to complete the entire task',
  },
  profile: {
    title: 'My Profile',
    userInformation: 'User Information',
    name: 'Name:',
    email: 'Email:',
    company: 'Company:',
    role: 'Role:',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    passwordRequirement: 'Password must be at least 6 characters',
    changing: 'Changing...',
  },
  admin: {
    adminPanel: 'Admin Panel',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    back: 'Back',
    loadingData: 'Loading data...',
    actions: 'Actions',
    notAvailable: 'N/A',
    active: 'Active',
    inactive: 'Inactive',
    company: 'Company',
    companies: 'Companies',
    name: 'Name',
    email: 'Email',
    password: 'Password',
    role: 'Role',
    description: 'Description',
    language: 'Language',
    requiredFieldsHint: '* Required fields',
    causalePanel: 'Causale Panel',
    licenseUsage: 'License Usage',
    used: 'Used',
    remaining: 'Remaining',
    total: 'Total',
    percentageUsed: 'used',
    listUsers: 'List Users',
    listConfigs: 'List Configurations',
    newUser: 'New User',
    manageTests: 'Manage Tests',
    users: 'Users',
    userSingular: 'user',
    userPlural: 'users',
    foundSingular: 'found',
    foundPlural: 'found',
    noRole: 'No role',
    noCompany: 'No company',
    myCompany: 'My Company',
    usersLoadError: 'Could not load users',
    usersFilterError: 'Could not filter users',
    userDeleteError: 'Could not delete user',
    usersBulkDeleteError: 'Could not delete selected users',
    loadingUsers: 'Loading users...',
    searchUsers: 'Search by name, email, or role',
    allCompanies: 'All companies',
    selectAll: 'Select all',
    deleteSelected: 'Delete selected',
    selected: 'selected',
    selectedPluralSuffix: 's',
    selectItem: 'Select item',
    cannotDeleteOwnAccount: 'You cannot delete your own account',
    noUsersFound: 'No users found',
    deleteUser: 'Delete user',
    deleteUserMessage: 'Are you sure you want to delete {name}? This action cannot be undone.',
    deleteSelectedUsers: 'Delete selected users',
    deleteSelectedUsersMessage: 'Are you sure you want to delete {count} user{plural}? This action cannot be undone.',
    viewDetails: 'View details',
    addUser: 'Add User',
    loadUserDataError: 'Could not load user data',
    userWithoutCompany: 'User without associated company',
    insufficientPermission: 'Insufficient permission to add users',
    noAccessLevelAvailable: 'No access level available',
    noCompanyAvailable: 'No company available',
    loadDataPermissionError: 'Could not load data. Check your permissions.',
    fillName: 'Please fill in the name',
    fillEmail: 'Please fill in the email',
    fillPassword: 'Please fill in the password',
    selectAccessLevel: 'Please select an access level',
    selectCompany: 'Please select a company',
    createUserError: 'Could not create user. Check the data and try again.',
    userAdded: 'User added successfully.',
    userUpdated: 'User updated successfully.',
    userRemoved: 'User removed successfully.',
    loadDataErrorTitle: 'Error loading data',
    noCompaniesPermission: 'No company available. Check your permissions.',
    noAccessLevelPermission: 'No access level available. Check your permissions.',
    accessLevel: 'Access Level',
    creating: 'Creating...',
    createUser: 'Create User',
    editUser: 'Edit User',
    loadingUserData: 'Loading user data...',
    nameRequired: 'Name is required',
    emailRequired: 'Email is required',
    roleRequired: 'Role is required',
    companyRequired: 'Company is required',
    updateUserError: 'Could not update user. Please try again.',
    loadingRoles: 'Loading roles...',
    saveChanges: 'Save Changes',
    testConfigs: 'Test Configurations',
    configSingular: 'configuration',
    configPlural: 'configurations',
    registeredSingular: 'registered',
    registeredPlural: 'registered',
    configLoadUserError: 'Could not load user data',
    configLoadError: 'Could not load configurations',
    loadingConfigs: 'Loading configurations...',
    searchConfigs: 'Search by value, currency, or language',
    delayedValue: 'Delayed Value',
    immediateValue: 'Immediate Value',
    currency: 'Currency',
    createdAt: 'Created at',
    noConfigFound: 'No configuration found',
    createFirstConfig: '+ Create first configuration',
    configCreateTitle: 'Test Application Settings',
    createConfigSuccess: 'Configuration added successfully!',
    createConfigError: 'Could not add configuration. Check the data and try again.',
    configAdded: 'Configuration added successfully.',
    configUpdated: 'Configuration updated successfully.',
    configRemoved: 'Configuration removed successfully.',
    addConfig: 'Add Configuration',
    createConfig: 'Create Configuration',
    configNotFound: 'Configuration not found',
    configLoadErrorShort: 'Could not load configuration',
    fillAllFields: 'Please fill in all fields!',
    configIdNotFound: 'Error: configuration ID not found!',
    updateConfigError: 'Could not update configuration. Check the data and try again.',
    loadingConfig: 'Loading configuration...',
    editConfig: 'Edit Configuration',
    removeConfig: 'Remove configuration',
    confirmRemoveConfig: 'Are you sure you want to remove configuration {name}?',
    deleteConfigError: 'Could not remove configuration.',
    appliedTests: 'Manage Applied Tests',
    searchTests: 'Search by name, record ID, or company',
    clearSearch: 'Clear search',
    totalPrefix: 'Total:',
    loadingTests: 'Loading tests...',
    testLoadError: 'Could not load tests.',
    testSingular: 'test',
    testPlural: 'tests',
    noTestsForFilters: 'No tests found for the applied filters',
    noTestsFound: 'No tests found',
    testsWillAppear: 'Applied tests will appear here',
    rowsPerPage: 'Rows per page',
    confirmDelete: 'Confirm Deletion',
    confirmDeleteTestQuestion: 'Are you sure you want to delete this test?',
    irreversibleAction: 'This action cannot be undone',
    testDeletedSuccess: 'Test "{name}" deleted successfully!',
    testDeleteError: 'Could not delete test. Please try again.',
    recordId: 'Record ID',
    time: 'Time',
    ED50: 'ED50',
    K: 'K',
    companiesRegistered: 'registered companies',
    addCompany: 'Add Company',
    activePlural: 'Active',
    licenses: 'Licenses',
    usedShort: 'Used',
    searchCompanies: 'Search by company, description, or CNPJ',
    loadingCompanies: 'Loading companies...',
    loadCompaniesError: 'Could not load companies.',
    status: 'Status',
    createdIn: 'Created at',
    noDescription: 'No description',
    editCompany: 'Edit company',
    removeCompany: 'Remove company',
    noCompaniesFound: 'No companies found',
    addCompanyTitle: 'Add Company',
    editCompanyTitle: 'Edit Company',
    close: 'Close',
    noLanguage: 'No language',
    activePlan: 'Active plan',
    savingCompany: 'Saving...',
    removeCompanyTitle: 'Remove Company',
    companyHasTests: 'Company {name} has {count} linked tests.',
    confirmRemoveCompany: 'Are you sure you want to remove {name}?',
    companyTestsPreservedWarning: 'The company will be removed from the list, but tests will be preserved in history.',
    typeCompanyName: 'Type the company name',
    removing: 'Removing...',
    companyNameRequired: 'Company name is required.',
    licensesInvalid: 'Licenses must be an integer greater than or equal to zero.',
    companyUpdated: 'Company updated successfully.',
    companyAdded: 'Company added successfully.',
    saveCompanyError: 'Could not save company.',
    typeExactCompanyName: 'Type the exact company name to confirm removal.',
    companyRemovedWithHistory: 'Company removed from the list and tests preserved in history.',
    companyRemoved: 'Company removed successfully.',
    explicitConfirmationRequired: 'Explicit confirmation required.',
    removeCompanyError: 'Could not remove company.',
    unlimited: 'Unlimited',
  },
};
